<template>
    <div class="relative flex flex-col h-full group" :class="props.class">
        <header class="pb-2">
            <p class="mb-2 text-xs">{{ report.label }}</p>
            <div class="flex items-center justify-between">
                <div v-if="presets" class="flex space-x-2">
                    <template v-for="preset in presets" :key="preset.key">
                        <Button
                            v-if="preset.type === 'button'"
                            size="sm"
                            type="button"
                            :color="selectedPreset && preset.key === selectedPreset.key ? 'brand' : 'default'"
                            @click="selectPreset(preset)"
                        >
                            {{ preset.name }}</Button
                        >
                        <CustomPreset
                            v-else-if="preset.type === 'custom'"
                            :preset="preset"
                            :selected-preset="selectedPreset"
                            @select="(customPreset) => selectPreset(customPreset)"
                        />
                    </template>
                </div>
                <div v-if="report.settings.type === 'line'" class="flex space-x-2">
                    <Button size="sm" type="button" @click="zoomChart('in')">+</Button>
                    <Button size="sm" type="button" @click="zoomChart('out')">-</Button>
                    <Button size="sm" type="button" @click="panChart('left')">
                        <Icon icon="hero/outline/chevron-left" class="w-3 h-3" />
                    </Button>
                    <Button size="sm" type="button" @click="panChart('right')">
                        <Icon icon="hero/outline/chevron-right" class="w-3 h-3" />
                    </Button>
                    <Button v-if="allColumns.length" size="sm" type="button" @click="changingColumns = true">
                        <Icon icon="hero/outline/cog" class="w-3 h-3" />
                    </Button>
                </div>
            </div>
        </header>
        <div class="relative flex-1" @dblclick="resetZoom">
            <!-- Loading -->
            <div
                v-if="loading"
                class="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-white/80 dark:bg-zinc-700/70"
            >
                <Loading :loading="true" size="xl" />
            </div>
            <Button
                v-if="isZoomed && isZoomableChart"
                size="sm"
                type="button"
                class="absolute top-1 right-0 z-10"
                @click="resetZoom"
            >
                {{ lang('reports::reports.actions.reset_zoom') }}
            </Button>
            <div v-if="report.settings.type === 'table'" class="flex overflow-y-auto">
                <div class="flex-auto">
                    <stats-table
                        :dimensions="dimensions"
                        :rows="preview"
                        :order-by="orderBy"
                        :order-way="orderWay"
                        @order="order"
                    ></stats-table>
                </div>
            </div>
            <div v-else-if="report.settings.type === 'grouped_table'" class="flex overflow-y-auto">
                <div class="flex-auto">
                    <grouped-stats-table
                        :dimensions="dimensions"
                        :rows="preview"
                        :group-by="report.settings.groupBy"
                        :order-by="orderBy"
                        :order-way="orderWay"
                        @order="order"
                    />
                </div>
            </div>
            <div v-else-if="report.settings.type === 'line'" class="h-full" :class="props.chartClass">
                <Line ref="chart" :data="reportDataChartJs.data" :options="reportDataChartJs.options" />
            </div>
            <div v-else-if="report.settings.type === 'bar'" class="h-full" :class="props.chartClass">
                <Bar ref="chart" :data="reportDataChartJs.data" :options="reportDataChartJs.options" />
            </div>
            <div v-else-if="report.settings.type === 'pie'" class="h-full" :class="props.chartClass">
                <Pie ref="chart" :data="reportDataChartJs.data" :options="reportDataChartJs.options" />
            </div>
            <div v-else-if="report.settings.type === 'doughnut'" class="h-full" :class="props.chartClass">
                <Doughnut ref="chart" :data="reportDataChartJs.data" :options="reportDataChartJs.options" />
            </div>
            <div
                v-else-if="report.settings.type === 'temperature'"
                class="flex items-center justify-center h-64"
                :class="props.chartClass"
            >
                <Temperature :data="reportDataChartJs.data" />
            </div>
            <div
                v-else-if="report.settings.type === 'gauge'"
                class="flex items-center justify-center"
                :class="props.chartClass"
            >
                <Gauge :data="reportDataChartJs.data" />
            </div>
        </div>
    </div>

    <!-- Column edit modal -->
    <Modal :show="changingColumns" max-width="6xl" :closeable="true" @close="changeColumns">
        <div class="grid grid-cols-3 gap-4 sm:grid-cols-4 lg:grid-cols-5">
            <div
                v-for="column in allColumns"
                :key="column.column"
                class="p-2 rounded-md cursor-pointer"
                :class="{
                    'bg-brand-400 shadow dark:text-brand-800': columnIsSelected(column),
                    'bg-gray-100 shadow-inner dark:bg-zinc-800 dark:text-zinc-500': !columnIsSelected(column),
                }"
                @click="toggleColumn(column)"
            >
                <p class="text-sm">
                    {{ column.column }}
                </p>
                <p class="text-xs">
                    {{ column.name }}
                </p>
            </div>
        </div>
    </Modal>
</template>

<script lang="ts" setup>
import { onMounted, ref, toRef, watch, onUnmounted, Ref } from 'vue';
import { Report } from '@src/Reports/Pages/types';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    BarElement,
    ArcElement,
    Legend,
    TimeScale,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'chartjs-adapter-luxon';
import { Line, Bar, Pie, Doughnut } from 'vue-chartjs';
import zoomPlugin from 'chartjs-plugin-zoom';
import Temperature from '@src/Reports/Pages/Partials/Temperature.vue';
import Gauge from '@src/Reports/Pages/Partials/Gauge.vue';
import StatsTable from '@src/Reports/Pages/Partials/StatsTable.vue';
import GroupedStatsTable from '@src/Reports/Pages/Partials/GroupedStatsTable.vue';
import CustomPreset from '@src/Reports/Pages/Partials/CustomPreset.vue';
import { Way } from '@src/Reports/Pages/enums';
import { ReportData } from '@src/Reports/Pages/ReportData';

ChartJS.register(
    TimeScale,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin,
);

interface Props {
    report?: Report;
    variables?: any;
    class?: string;
    chartClass?: string;
}

interface ChartDataRequestParams {
    variables?: any | [];
    settings?: object;
    preset?: string | number;
    columns?: any | [];
    cached?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    report: null,
    variables: [],
    class: '',
    chartClass: '',
});

const loading = ref(false);

const report = toRef(props, 'report');

const variables = toRef(props, 'variables');

const presets = ref(report.value.presets);
const selectedPreset = ref(null);

const allColumns = ref(report.value.columns ?? []);
const defaultColumns = allColumns.value.filter((single) => {
    return single.default === '1';
});
const selectedColumns = ref(defaultColumns);
const changingColumns = ref(false);

const chart = ref(null);

const isZoomed = ref(false);

const isZoomableChart = ['line', 'bar'].includes(props.report?.settings?.type);

const preview = ref([]);
const dimensions = ref([]);

const orderBy: Ref<string> = ref(null);
const orderWay: Ref<Way> = ref(null);

/**
 * ReportData
 */
const reportData = new ReportData();

const resolveStackedSetting = (): boolean => {
    if (typeof report.value.settings.stacked === 'boolean') {
        return report.value.settings.stacked;
    }

    return report.value.settings.type === 'bar';
};

const reportSettings = report.value.settings ?? {};
const reportAxis = reportSettings.axis ?? { x: null, y: [] };
reportData.setType(reportSettings.type);
reportData.setAxis(reportAxis.x, 'x');
reportData.setAxis(reportAxis.y, 'y');
reportData.setCategory(reportSettings.category);
reportData.setCategories(reportSettings.categories);
reportData.setFormatting(reportSettings.formatting);
reportData.setDisplaySettings({
    stacked: resolveStackedSetting(),
    yMin: report.value.settings.yMin,
    yMax: report.value.settings.yMax,
});
reportData.setZoomStateCallback((chartInstance) => {
    isZoomed.value = !!chartInstance?.isZoomedOrPanned?.();
});

const reportDataChartJs = ref(reportData.getChartJs());

if (reportData.hasMultipleCategories()) {
    ChartJS.register(ChartDataLabels);
}

const refreshChartData = async (params: ChartDataRequestParams = null) => {
    loading.value = true;

    return await getChartData(
        report.value.id,
        params ? params.variables : [],
        params ? params.settings : {},
        params && params.preset ? params.preset : selectedPreset.value,
        params && params.columns ? params.columns : selectedColumns.value,
        params && params.cached ? params.cached : false,
    ).then(({ data }) => {
        // TODO: refactor
        if (data && data.dimensions) {
            if (dimensions.value.length && selectedColumns.value.length) {
                const oldDimensions = dimensions.value;
                const newDimensions = selectedColumns.value;

                // Added columns
                const add = newDimensions.filter((newDimension) => {
                    const name = newDimension.name ?? newDimension.column;
                    return oldDimensions.findIndex((oldDimension) => oldDimension.column === name) === -1;
                });
                add.forEach((single) => {
                    report.value.settings.axis.y.push({
                        column: single.name ?? single.column,
                        backgroundColor: single.backgroundColor ?? getRandomColor(),
                        boolean: single.boolean,
                    });
                });

                // Removed columns
                const remove = oldDimensions.filter((oldDimension) => {
                    return (
                        newDimensions.findIndex((newDimension) => {
                            const name = newDimension.name ?? newDimension.column;
                            return name === oldDimension.column;
                        }) === -1
                    );
                });
                remove.forEach((single) => {
                    const index = report.value.settings.axis.y.findIndex((scale) => {
                        return scale.column === single.column;
                    });
                    if (index !== -1) {
                        report.value.settings.axis.y.splice(index, 1);
                    }
                });
            }
            dimensions.value = data.dimensions;
        } else {
            dimensions.value = [];
        }

        if (data && data.preset) {
            selectedPreset.value = data.preset;
        }

        reportData.setAxis(report.value.settings.axis.x, 'x');
        reportData.setAxis(report.value.settings.axis.y, 'y');
        reportData.setDisplaySettings({
            stacked: resolveStackedSetting(),
            yMin: report.value.settings.yMin,
            yMax: report.value.settings.yMax,
        });

        reportData.setRawData(data.preview);

        reportData.setLabelsFromData();

        preview.value = data.preview;

        reportDataChartJs.value = reportData.getChartJs();

        loading.value = false;

        return data;
    });
};

const prepareVariables = (localVariables: any) => {
    let result = {};

    Object.keys(variables.value).forEach((key) => {
        if (variables.value[key]) {
            result[key] = variables.value[key].value;
        }
    });

    if (orderBy.value) {
        result.orderBy = orderBy.value;
    }

    if (orderWay.value) {
        result.orderWay = orderWay.value;
    }

    result = {
        ...result,
        ...localVariables,
    };

    return result;
};

const getChartData = async (
    id: number,
    localVariables: any,
    localSettings: any,
    localPreset: any,
    localColumns: any,
    localCached: boolean = false,
) => {
    return axios.post(route('reports.admin.api.data', { id }), {
        variables: prepareVariables(localVariables),
        settings: localSettings,
        preset: localPreset,
        columns: localColumns,
        cached: localCached,
    });
};

const getSlug = (echo, key, value) => {
    if (!echo) {
        return '';
    }

    return echo.replace(`:${key}`, value).replace(/:+/g, '-').trim();
};

const startListening = (locals = {}) => {
    Object.keys(locals).forEach((variable) => {
        const slug = getSlug(locals[variable].echo, variable, locals[variable].value);

        console.log(`%cChart ${report.value.id} starts listening on: ${slug}`, 'background-color: #ecfccb');

        window.Echo.channel(slug).listen('.data.normalized', ({ registers }) => {
            if (registers === '100140') {
                refreshChartData();
            }
        });
    });
};

const order = ({ column, way }) => {
    orderBy.value = column;
    orderWay.value = way;

    refreshChartData();
};

const stopListening = (locals = {}) => {
    Object.keys(locals).forEach((variable) => {
        const slug = getSlug(locals[variable].echo, variable, locals[variable].value);

        console.log(`%cChart ${report.value.id} stops listening on: ${slug}`, 'background-color: #e0f2fe');

        window.Echo.channel(slug).stopListening('.data.normalized');
    });
};

const initializeForVariables = (newVariables = {}, oldVariables = {}) => {
    startListening(newVariables);
    stopListening(oldVariables);
};

const selectPreset = (clickedPreset) => {
    let preset = clickedPreset;
    if (preset && selectedPreset.value && preset.type !== 'custom' && selectedPreset.value.key === preset.key) {
        preset = null;
    }
    selectedPreset.value = preset;
    refreshChartData({
        preset,
    });
    resetZoom();
};

const resetZoom = () => {
    chart.value?.chart?.resetZoom();
    isZoomed.value = false;
};

const syncZoomState = () => {
    isZoomed.value = !!chart.value?.chart?.isZoomedOrPanned();
};

const zoomChart = (direction) => {
    if (direction === 'in') {
        chart.value.chart.zoom(1.1);
    } else if (direction === 'out') {
        chart.value.chart.zoom(0.9);
    }
    syncZoomState();
};

const panChart = (direction) => {
    if (direction === 'left') {
        chart.value.chart.pan({ x: 100 }, undefined, 'default');
    } else if (direction === 'right') {
        chart.value.chart.pan({ x: -100 }, undefined, 'default');
    }
    syncZoomState();
};

const columnIsSelected = (column) => {
    return typeof selectedColumns.value.find((single) => single.column === column.column) !== 'undefined';
};

const toggleColumn = (column) => {
    const index = selectedColumns.value.findIndex((single) => single.column === column.column);
    if (index !== -1) {
        selectedColumns.value.splice(index, 1);
    } else {
        selectedColumns.value.push(column);
    }
};

const changeColumns = () => {
    changingColumns.value = false;

    refreshChartData({ columns: selectedColumns.value });
};

const getRandomColor = () => {
    const number = (Math.random() * 0xfffff * 1000000).toString(16);
    return `#${number.slice(0, 6)}`;
};

onMounted(async () => {
    const { activeColumns, activePreset } = report.value.settings;
    const params = {
        columns: activeColumns ? activeColumns : defaultColumns,
    };
    if (activePreset) {
        params.preset = activePreset;
        selectedPreset.value = activePreset;
    }
    selectedColumns.value = params.columns;
    refreshChartData(params);
});

onUnmounted(() => {
    stopListening(variables.value);
});

watch(variables, (value, oldValue) => {
    refreshChartData();
    initializeForVariables(value, oldValue);
});

initializeForVariables();

defineExpose({
    refreshChartData,
});
</script>
